/**
 * Remote KV storage backed by POST /api/kv (same session cookies as /auth).
 * Used when REACT_APP_USE_SERVER_STORAGE=true and DATABASE_URL is configured on the API.
 */
const STORAGE_RATE_LIMIT_BACKOFF_MS = 60000;
let storageRateLimitUntil = 0;
let readOnlyWarned = false;

/** Read the CSRF token stored by AuthGate after login. */
function getCsrfToken() {
  return (typeof window !== "undefined" && window.__csrfToken) || "";
}

export function isServerStorageRateLimited() {
  return Date.now() < storageRateLimitUntil;
}

export function createServerStorage(apiBase) {
  const base = apiBase || "";

  function noteRateLimit(res) {
    if (res && res.status === 429) {
      storageRateLimitUntil = Math.max(storageRateLimitUntil, Date.now() + STORAGE_RATE_LIMIT_BACKOFF_MS);
    }
  }

  async function get(key) {
    if (isServerStorageRateLimited()) throw new Error("Too many requests. Please wait a minute and try again.");
    const res = await fetch(`${base}/api/kv/${encodeURIComponent(key)}`, { credentials: "include" });
    noteRateLimit(res);
    if (res.status === 404) throw new Error(`Key not found: ${key}`);
    if (!res.ok) throw new Error(`Key not found: ${key}`);
    const data = await res.json();
    return { key, value: data.value };
  }

  async function set(key, value) {
    // No client-side rate-limit pre-check for writes — user-initiated saves must always
    // attempt the request. The server enforces its own limits and returns 429 if needed.
    const res = await fetch(`${base}/api/kv/${encodeURIComponent(key)}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", "x-csrf-token": getCsrfToken() },
      body: JSON.stringify({ value }),
    });
    noteRateLimit(res);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Read-only test build (src/readOnlyGuard.js answered the write with a 403): the app's automatic
      // saves (status sweeps, link masks, audit log …) run from effects that never catch, so a thrown
      // error here surfaced as an "Uncaught runtime error" overlay (2026-09-06). The red READ-ONLY
      // strip already says nothing persists — resolve quietly and note it in the console instead.
      if (err && err.readOnly === true) {
        if (!readOnlyWarned) {
          readOnlyWarned = true;
          // eslint-disable-next-line no-console
          console.warn(`[read-only] KV writes are dropped in this build (first: ${key}). ${err.error || ""}`);
        }
        return { key, value, readOnly: true };
      }
      throw new Error(err.error || "KV write failed");
    }
    return { key, value };
  }

  async function del(key) {
    await fetch(`${base}/api/kv/${encodeURIComponent(key)}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "x-csrf-token": getCsrfToken() },
    });
    return { key, deleted: true };
  }

  return {
    get,
    set,
    delete: del,
    list: async () => ({ keys: [] }),
  };
}
